/**
 * WIPEOUT entity → `RenderableEntity` adapter (TYPED).
 *
 * DEFUSE Direction-3 — typed sibling of `../wipeoutToRenderable.ts`. Reads the
 * canonical `WipeoutEntity` fields directly off the model (`model.ts`:
 * `wipeoutEntitySchema` `vertices` — a `drawingPointSchema[]`) instead of the
 * legacy `EntityPreview.properties` string bag. Produces a byte-identical
 * `RenderableEntity` (golden-parity) for the same entity by calling the SAME
 * shared `buildWipeoutSceneGeometry` builder the legacy adapter now calls.
 *
 * # z handling — NO superset divergence
 *
 * `wipeoutEntitySchema.vertices` are 2D (`drawingPointSchema` has only x/y),
 * AND the legacy bag drops z (`buildWipeoutPreview` emits only `Vertex N X` /
 * `Vertex N Y`). Both paths therefore feed `z: 0` corners — unlike Line/Point
 * there is no richer typed z to assert as a documented superset.
 *
 * # Reject paths (identical to legacy, centralised in the shared builder)
 *
 *   - Fewer than 3 corners → null (the schema's `.min(3)` makes this rare, but
 *     the builder still guards it, matching the legacy `< 3` reject).
 *   - Collinear / zero-area contour → null (shared signed-area reject).
 *
 * The legacy non-finite-coordinate reject is a bag-specific concern (parsing
 * arbitrary strings); the typed model guarantees finite numbers via zod, so
 * that branch is unreachable here.
 *
 * Category B: standalone file; NOT imported into any render path yet (the
 * typed dispatcher case is added in a later phase).
 */

import type { WipeoutEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import { buildRenderableShell, type AdapterContext } from "../adapterContext";
import { buildWipeoutSceneGeometry } from "../geom/wipeoutGeometry";

/** Typed adapter for the `Wipeout` entity (`wipeoutEntitySchema`). */
export function wipeoutFromEntity(
  entity: WipeoutEntity,
  ctx: AdapterContext,
): RenderableEntity | null {
  // `vertices` are 2D (`drawingPointSchema`); the legacy bag also drops z, so
  // feed z: 0 — identical pre-offset corners to the legacy path.
  const corners: Vec3[] = entity.vertices.map((v) => ({
    x: v.x,
    y: v.y,
    z: 0,
  }));

  // Shared one-truth: applyOffset per corner, collinearity reject, geometry +
  // AABB. Returns null when the contour cannot paint an interior.
  const result = buildWipeoutSceneGeometry(corners);
  if (!result) {
    return null;
  }

  return buildRenderableShell(entity, ctx, result.geometry, result.bounds);
}
